import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Backpack,
  GameController,
  Gift,
  Lock,
  Pants,
  Smiley,
  Sneaker,
  Sunglasses,
  TShirt,
  ArrowLeft,
  SpeakerHigh,
  User,
  Palette,
  Cat,
  Sparkle,
  Diamond,
  HandPalm,
  BaseballCap,
  Heart
} from "@phosphor-icons/react";
import AvatarCanvas, { type AvatarCanvasRef } from './components/AvatarCanvas';
import ItemPalette from './components/ItemPalette';
import { SaveAvatarButton } from './components/SaveAvatarButton';
import { AvatarKonvaModal } from './components/AvatarKonvaModal';
import { initialAvatarState, availableItems } from './data/index';
import { useFunifierItems } from './hooks/useFunifierItems';
import { useLoadUserAvatar } from './hooks/useLoadUserAvatar';
import { getZIndexForCategory, debugZIndexConfig, debugItemsZIndex } from './utils/zIndexManager';
import { useAppStore } from '../../store';
import type { AvatarComposition, AvatarItem } from './types/types';
import './Teensmart.css';

const CATEGORY_ORDER = [
  'skin',
  'face',
  'hair',
  'shirt',
  'pants',
  'shoes',
  'glasses',
  'facial-hair',
  'pets',
  'hat',
  'hand-accessories',
  'body-accessories',
  'bag',
  'accessories',
  'lock',
  'gamepad',
  'gift',
  'background',
  'body',
  'emotion',
  'game',
  'special'
];

const getCategoryImage = (category: string, items: AvatarItem[]): string => {
  const categoryItems = items.filter(item => item.category === category);
  if (categoryItems.length > 0) {
    const firstItem = categoryItems[0];
    return firstItem.catalogImage || firstItem.src;
  }
  return '/body/boneco.png';
};

interface AvatarKonvaProps {
  onSaveSuccess?: () => void;
}

const AvatarKonva: React.FC<AvatarKonvaProps> = ({ onSaveSuccess }) => {
  const [avatarComposition, setAvatarComposition] = useState<AvatarComposition>(initialAvatarState);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [selectedItemForPurchase, setSelectedItemForPurchase] = useState<AvatarItem | null>(null);
  const { items: funifierItems, loading, error } = useFunifierItems();
  
  
  const { user } = useAppStore();
  
  
  const { 
    avatarComposition: savedAvatarComposition, 
    hasExistingAvatar, 
    isLoading: isLoadingAvatar,
    error: avatarLoadError 
  } = useLoadUserAvatar();
  
  
  const canvasRef = useRef<AvatarCanvasRef>(null);
  
  // Track whether the initial load has already been applied
  const hasAppliedSavedComposition = useRef(false);

  useEffect(() => {
    // Only apply saved composition on the FIRST successful load
    // This prevents refreshUser() from wiping unsaved selections after a purchase
    if (!isLoadingAvatar && savedAvatarComposition.length > 0 && !hasAppliedSavedComposition.current) {
      setAvatarComposition(savedAvatarComposition);
      hasAppliedSavedComposition.current = true;
    }
  }, [isLoadingAvatar, savedAvatarComposition]);

  const itemsToShow = funifierItems;

  
  const allAvailableItems = useMemo(() => {
    
    const localSkinItems = availableItems.filter(item => item.category === 'skin');
    
    
    const filteredFunifierItems = itemsToShow.filter(item => item.category !== 'effects');
    
                    
    const combined = [...filteredFunifierItems, ...localSkinItems];
        return combined;
  }, [itemsToShow]);

  
  const categories = useMemo(() => {
    
    const localSkinItems = availableItems.filter(item => item.category === 'skin');
    const localCategories = localSkinItems.map(item => item.category);
    
    const funifierCategories = allAvailableItems.length > 0 ? 
      Array.from(new Set(allAvailableItems.map(item => item.category))) : [];
    
  
  const EXCLUDED_CATEGORIES: string[] = [];

  let allDetectedCategories = Array.from(new Set([...localCategories, ...funifierCategories]));
  allDetectedCategories = allDetectedCategories.filter(cat => !EXCLUDED_CATEGORIES.includes(cat));
      
    if (funifierCategories.length === 0) {
      
      const basicCategories = CATEGORY_ORDER.slice(0, 9).filter(c => !EXCLUDED_CATEGORIES.includes(c)); 
            return basicCategories;
    }
    
    
    const orderedCategories: string[] = [];
    
    
    CATEGORY_ORDER.forEach(category => {
      if (allDetectedCategories.includes(category)) {
        orderedCategories.push(category);
      }
    });
    
    
  const extraCategories = allDetectedCategories.filter(cat => !CATEGORY_ORDER.includes(cat));
    orderedCategories.push(...extraCategories);
    
            return orderedCategories;
  }, [allAvailableItems]);
  
  
  useEffect(() => {
    if (categories.length > 0) {
            categories.forEach(cat => {
              });
    }
  }, [categories, allAvailableItems]);

  
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  
  const filteredItems = selectedCategory
    ? allAvailableItems.filter(item => item.category === selectedCategory)
    : [];

  
  const handlePurchaseRequest = (item: AvatarItem) => {
    setSelectedItemForPurchase(item);
    setPurchaseModalOpen(true);
  };

  
  const handleClosePurchaseModal = () => {
    setPurchaseModalOpen(false);
    setSelectedItemForPurchase(null);
  };

  
  const handlePurchaseSuccess = (purchasedItem: AvatarItem) => {
                
    
    handleItemSelect(purchasedItem);
    
      };

  
  const handleItemRemove = (category: string) => {
        setAvatarComposition(prevComposition => {
      const filteredComposition = prevComposition.filter(
        item => item.category !== category
      );
      
            return filteredComposition;
    });
  };

  
  const handleItemSelect = (selectedItem: AvatarItem) => {
    setAvatarComposition(prevComposition => {
      
      if (selectedItem.category === 'skin') {
        const filteredComposition = prevComposition.filter(
          item => item.category !== 'base' && item.category !== 'skin'
        );
        
        const newItem: AvatarItem = {
          ...selectedItem,
          id: `skin_${Date.now()}`,
          category: 'base' 
        };
        
        return [...filteredComposition, newItem];
      }
      
      
      const filteredComposition = prevComposition.filter(
        item => item.category !== selectedItem.category || item.category === 'base'
      );
      
      
      const newItem: AvatarItem = {
        ...selectedItem,
        id: `${selectedItem.category}_${Date.now()}`,
        
        zIndex: getZIndexForCategory(selectedItem.category)
      };
      
      const newComposition = [...filteredComposition, newItem];
      
      
            debugItemsZIndex(newComposition);
      
      return newComposition;
    });
  };

  
  const handleItemDragEnd = (itemId: string, newPos: { x: number; y: number }) => {
    setAvatarComposition(prevComposition =>
      prevComposition.map(item =>
        item.id === itemId
          ? { ...item, x: newPos.x, y: newPos.y }
          : item
      )
    );
  };

  
  const getCategoryIcon = (category: string) => {
    const iconMap: { [key: string]: JSX.Element } = {
      
      'skin': <Palette className="category-phosphor-icon" />,        
      'face': <Smiley className="category-phosphor-icon" />,         
      'hair': <User className="category-phosphor-icon" />,           
      'shirt': <TShirt className="category-phosphor-icon" />,        
      'pants': <Pants className="category-phosphor-icon" />,         
      'shoes': <Sneaker className="category-phosphor-icon" />,       
      'head_props': <Sunglasses className="category-phosphor-icon" />,  
      'facial_hair': <User className="category-phosphor-icon" />,    
      'pets': <Cat className="category-phosphor-icon" />,            
      'hat': <BaseballCap className="category-phosphor-icon" />,     
      'hand_props': <HandPalm className="category-phosphor-icon" />, 
      'body_props': <Backpack className="category-phosphor-icon" />, 
      'bag': <Backpack className="category-phosphor-icon" />,        
      
      
      'accessories': <Diamond className="category-phosphor-icon" />, 
      'lock': <Lock className="category-phosphor-icon" />,           
      'gamepad': <GameController className="category-phosphor-icon" />, 
      'gift': <Gift className="category-phosphor-icon" />,           
      'background': <Sparkle className="category-phosphor-icon" />,  
      'body': <User className="category-phosphor-icon" />,           
      'emotion': <Smiley className="category-phosphor-icon" />,      
      'game': <GameController className="category-phosphor-icon" />, 
      'special': <Heart className="category-phosphor-icon" />        
    };
    
    return iconMap[category] || <Backpack className="category-phosphor-icon" />;
  };

  
  if (isLoadingAvatar) {
    return (
      <div className="avatar-creator">
        <div className="main-content">
          <div className="loading-avatar-message" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold' }}>
              Carregando...
            </p>
          </div>
        </div>
      </div>
    );
  }

  
  if (avatarLoadError) {
    return (
      <div className="avatar-creator">
        <div className="main-content">
          <div className="error-avatar-message" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <div style={{ fontSize: '48px' }}>⚠️</div>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: 'red' }}>Erro ao carregar avatar</p>
            <p style={{ fontSize: '14px', color: '#666' }}>{avatarLoadError}</p>
            <p style={{ fontSize: '12px', color: '#999' }}>Iniciando com avatar em branco...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="avatar-creator">
      { }
      
      <div className="main-content">
        <div className="avatar-preview">
          <div className="avatar-container">
            <AvatarCanvas
              ref={canvasRef}
              avatarComposition={avatarComposition}
              onItemDragEnd={handleItemDragEnd}
            />
          </div>
        </div>
        
        <div className="customization-area">
          
          {categories.length > 0 && (
            <div className="categories">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`category-button ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                  title={category.charAt(0).toUpperCase() + category.slice(1)}
                >
                  {getCategoryIcon(category)}
                </button>
              ))}
            </div>
          )}
          
          <div className="customization-options">
            <ItemPalette
              availableItems={filteredItems}
              onItemSelect={handleItemSelect}
              onPurchaseRequest={handlePurchaseRequest}
              onItemRemove={handleItemRemove}
              selectedCategory={selectedCategory}
            />
            
            {loading && filteredItems.length === 0 && selectedCategory !== 'skin' && (
              <div className="loading-message">
                <p>Carregando itens do Funifier Studio...</p>
              </div>
            )}
            
            {error && (
              <div className="error-message">
                <p>Erro ao carregar itens: {error}</p>
              </div>
            )}
          </div>

          {}
          <div className="save-section-fixed">
            <SaveAvatarButton
              avatarComposition={avatarComposition}
              canvasStageRef={canvasRef}
              onSaveSuccess={onSaveSuccess}
            />
          </div>
        </div>
      </div>

      {}
      <AvatarKonvaModal
        isOpen={purchaseModalOpen}
        onClose={handleClosePurchaseModal}
        item={selectedItemForPurchase}
        onPurchaseSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

export default AvatarKonva;
